/**
 * One more block on a booking in progress, priced — or why there is none
 * (Simulator Companion slice 9).
 *
 * Read while the booking's page is open and the booking can still run: the gap
 * before the next booking can close between reads, so the offer is refreshed on
 * focus and kept briefly. Returns the envelope unchanged (internalapi-envelope);
 * the row decides what a refusal or a failure looks like.
 *
 * @module booking/hooks/useExtensionOfferQuery
 */

import { useQuery } from '@tanstack/react-query';
import type { ApiExtensionOfferResponse } from '@lgc/database/types/domains/booking';
import { queryKeys } from '~/lib/query/keys';
import type { ApiResult } from '~/lib/apiClient/types';
import { bookingService } from '../api/bookingService';

export interface UseExtensionOfferQueryOptions {
  bookingId: number | null;
  enabled?: boolean;
}

export function useExtensionOfferQuery({ bookingId, enabled = true }: UseExtensionOfferQueryOptions) {
  const query = useQuery<ApiResult<ApiExtensionOfferResponse>>({
    queryKey: bookingId ? queryKeys.bookings.extensionOffer(bookingId) : queryKeys.disabled.extensionOffer(),
    queryFn: () => {
      if (!bookingId) throw new Error('Booking ID is required');
      return bookingService.getExtensionOffer(bookingId);
    },
    enabled: enabled && bookingId !== null,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const result = query.data;
  return {
    ...query,
    /** The offer, or null — null also while loading and after a failure. */
    offer: result?.success ? result.payload.offer : null,
    refusal: result?.success ? result.payload.refusal ?? null : null,
    isApiError: result !== undefined && !result.success,
    apiErrorMessage: result && !result.success ? result.message : null,
  };
}
